import { Action, ActionReducerMap, createFeatureSelector } from '@ngrx/store';
import { ArztEinschreibung, ArztEinschreibungOrdering } from '../api/models';
import {
  LoadingState,
  Ordering,
  resetOrAddPagesWRTOrdering,
  resetOrAddPageWRTPageSize,
} from '../../shared/reducer.includes';
import { PaginatedResult } from '../../shared/models.includes';

export enum ArztEinschreibungActionTypes {
  RefreshArztEinschreibungen = '[ArztEinschreibung] Lade ArztEinschreibungen komplett neu',
  LoadArztEinschreibungen = '[ArztEinschreibung] Lade ArztEinschreibungen',
  LoadingArztEinschreibungen = '[ArztEinschreibung] ArztEinschreibungen werden abgerufen',
  LastArztEinschreibungenPageLoaded = '[ArztEinschreibung] Letzte ArztEinschreibungen Seite erfolgreich abgerufen',
  ArztEinschreibungenLoaded = '[ArztEinschreibung] ArztEinschreibungen erfolgreich abgerufen',
}

export class RefreshArztEinschreibungenAction implements Action {
  readonly type = ArztEinschreibungActionTypes.RefreshArztEinschreibungen;

  constructor(public options: ArztEinschreibungOptions = { patientPk: null }) {}
}

export interface ArztEinschreibungOptions {
  patientPk?: number;
  force?: boolean;
}

export class LoadArztEinschreibungenPageAction implements Action {
  readonly type = ArztEinschreibungActionTypes.LoadArztEinschreibungen;

  constructor(
    public page: number,
    public pageSize: number = 10,
    public ordering: Ordering<ArztEinschreibungOrdering>,
    public options: ArztEinschreibungOptions = { patientPk: null },
  ) {}
}

export class LastArztEinschreibungenPageLoadedAction implements Action {
  readonly type =
    ArztEinschreibungActionTypes.LastArztEinschreibungenPageLoaded;

  constructor(
    public page: number,
    public pageSize: number = 10,
    public ordering: Ordering<ArztEinschreibungOrdering>,
    public options: ArztEinschreibungOptions = { patientPk: null },
  ) {}
}

export class LoadingArztEinschreibungenPageAction implements Action {
  readonly type = ArztEinschreibungActionTypes.LoadingArztEinschreibungen;

  constructor(
    public page: number,
    public pageSize: number = 10,
    public ordering: Ordering<ArztEinschreibungOrdering>,
    public options: ArztEinschreibungOptions = { patientPk: null },
  ) {}
}

export class ArztEinschreibungenPageLoadedAction implements Action {
  readonly type = ArztEinschreibungActionTypes.ArztEinschreibungenLoaded;

  constructor(
    public page: PaginatedResult<ArztEinschreibung, ArztEinschreibungOrdering>,
    public options: ArztEinschreibungOptions = { patientPk: null },
  ) {}
}

export type ArztEinschreibungActions =
  | LoadArztEinschreibungenPageAction
  | LoadingArztEinschreibungenPageAction
  | RefreshArztEinschreibungenAction
  | LastArztEinschreibungenPageLoadedAction
  | ArztEinschreibungenPageLoadedAction;

export type ArztEinschreibungPage = PaginatedResult<
  ArztEinschreibung,
  ArztEinschreibungOrdering
>;

export interface ArztEinschreibungenState {
  ordering: Ordering<ArztEinschreibungOrdering>;
  pageSize: number;
  loadingState: LoadingState;
  total: number;
  pages: { [key: number]: ArztEinschreibungPage };
  alleEinschreibungen: ArztEinschreibung[];
  einschreibungenFuerPatienten: { [key: number]: ArztEinschreibungenState };
}

export const initialState: ArztEinschreibungenState = {
  loadingState: LoadingState.Init,
  total: 0,
  pages: {},
  pageSize: 10,
  ordering: null,
  alleEinschreibungen: [],
  einschreibungenFuerPatienten: {},
};

function getState(
  state: ArztEinschreibungenState,
  action: ArztEinschreibungActions,
) {
  let s: ArztEinschreibungenState;
  let ret: ArztEinschreibungenState;
  if (action.options?.patientPk != null) {
    ret = {
      ...state,
      einschreibungenFuerPatienten: { ...state.einschreibungenFuerPatienten },
    };
    if (!ret.einschreibungenFuerPatienten[action.options.patientPk]) {
      ret.einschreibungenFuerPatienten[action.options.patientPk] = {
        ...initialState,
        einschreibungenFuerPatienten: {},
      };
    }
    s = { ...ret.einschreibungenFuerPatienten[action.options.patientPk] };
    ret.einschreibungenFuerPatienten[action.options.patientPk] = s;
  } else {
    s = {
      ...state,
    };
    ret = s;
  }
  return [s, ret];
}

export function arztEinschreibungenStateReducer(
  state: ArztEinschreibungenState = initialState,
  action: ArztEinschreibungActions,
): ArztEinschreibungenState {
  let s: ArztEinschreibungenState;
  let ret: ArztEinschreibungenState;
  switch (action.type) {
    case ArztEinschreibungActionTypes.LoadingArztEinschreibungen:
      [s, ret] = getState(state, action);
      s.loadingState = LoadingState.Loading;
      return ret;
    case ArztEinschreibungActionTypes.RefreshArztEinschreibungen:
      [s, ret] = getState(state, action);
      s.pages = {};
      // s.alleEinschreibungen = [];
      return ret;
    case ArztEinschreibungActionTypes.ArztEinschreibungenLoaded:
      [s, ret] = getState(state, action);
      s.total = action.page.count;
      if (!action.page.next) {
        s.loadingState = LoadingState.Loaded;
      }
      s.pages = { ...s.pages, [action.page.pageNumber]: action.page };

      let reset = resetOrAddPageWRTPageSize(s, action);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      reset = reset && resetOrAddPagesWRTOrdering(s, action);

      s.alleEinschreibungen = [];
      for (const existingPageNumber of Object.keys(s.pages)) {
        const pageData = s.pages[existingPageNumber] as ArztEinschreibungPage;
        s.alleEinschreibungen = s.alleEinschreibungen.concat(pageData.results);
      }
      return ret;
    default:
      return state;
  }
}

export const arztEinschreibungenStateName = 'arztEinschreibungFeature';

export interface ArztEinschreibungReducerState {
  arztEinschreibungen: ArztEinschreibungenState;
}

export const arztEinschreibungReducers: ActionReducerMap<ArztEinschreibungReducerState> =
  {
    arztEinschreibungen: arztEinschreibungenStateReducer,
  };

export const getArztEinschreibungFeatureState =
  createFeatureSelector<ArztEinschreibungReducerState>(
    arztEinschreibungenStateName,
  );
